import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ShoppingBag, X, ZoomIn } from "lucide-react";

const getGallery = (product: any) =>
  Array.from(
    new Set(
      ([product?.image_url, ...(product?.images ?? [])].filter(Boolean) as string[]),
    ),
  );

interface Props {
  product: any;
  /** Kept for backward compatibility — no longer affects images. */
  selectedColor?: string | null;
  activeImage: string | null;
  setActiveImage: (url: string) => void;
}

const ZOOM_LEVEL = 2.4;

export const ProductGallery = ({ product, activeImage, setActiveImage }: Props) => {
  const gallery = getGallery(product);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Desktop hover zoom state
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const activeIndex = Math.max(0, gallery.findIndex((u) => u === activeImage));

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [gallery.length]);

  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const wrap = imgWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setLensPos({ x, y });
    setImgSize({ w: rect.width, h: rect.height });
    const px = Math.max(0, Math.min(1, x / rect.width));
    const py = Math.max(0, Math.min(1, y / rect.height));
    setBgPos({ x: px * 100, y: py * 100 });
  };

  const openLightbox = (url?: string) => {
    if (url) setActiveImage(url);
    setLightboxOpen(true);
  };

  const next = useCallback(() => {
    if (gallery.length === 0) return;
    const i = (activeIndex + 1) % gallery.length;
    setActiveImage(gallery[i]);
  }, [activeIndex, gallery, setActiveImage]);

  const prev = useCallback(() => {
    if (gallery.length === 0) return;
    const i = (activeIndex - 1 + gallery.length) % gallery.length;
    setActiveImage(gallery[i]);
  }, [activeIndex, gallery, setActiveImage]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, next, prev]);

  const LENS_SIZE = 140;

  return (
    <div className="space-y-3">
      <div className="relative">
        <div
          ref={imgWrapRef}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          onMouseMove={onMouseMove}
          onClick={() => activeImage && openLightbox()}
          className="group relative aspect-square overflow-hidden rounded-3xl bg-white border border-border shadow-card flex items-center justify-center p-4 cursor-zoom-in select-none"
        >
          {activeImage ? (
            <>
              <img
                key={activeImage}
                src={activeImage}
                alt={product.name}
                loading="lazy"
                draggable={false}
                className="max-h-full max-w-full object-contain animate-fade-in pointer-events-none"
              />
              {/* Hover lens (desktop only) */}
              <div
                className="pointer-events-none absolute hidden md:block border-2 border-white/90 bg-white/20 backdrop-blur-[1px] rounded-full shadow-lg transition-opacity"
                style={{
                  width: LENS_SIZE,
                  height: LENS_SIZE,
                  left: lensPos.x - LENS_SIZE / 2,
                  top: lensPos.y - LENS_SIZE / 2,
                  opacity: hovering ? 1 : 0,
                }}
              />
              {/* Zoom hint */}
              <div className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/95 border border-border shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-4 w-4" />
              </div>
            </>
          ) : (
            <ShoppingBag className="h-20 w-20 text-muted-foreground/30" />
          )}
        </div>

        {/* Desktop zoom preview panel */}
        {activeImage && (
          <div
            className="hidden md:block absolute top-0 left-full ml-4 w-[460px] h-full rounded-2xl border border-border bg-white shadow-xl overflow-hidden pointer-events-none z-30 transition-opacity"
            style={{
              opacity: hovering ? 1 : 0,
              backgroundImage: `url("${activeImage}")`,
              backgroundRepeat: "no-repeat",
              backgroundSize: `${imgSize.w * ZOOM_LEVEL}px ${imgSize.h * ZOOM_LEVEL}px`,
              backgroundPosition: `${bgPos.x}% ${bgPos.y}%`,
              backgroundColor: "#fff",
            }}
          />
        )}
      </div>

      {gallery.length > 1 && (
        <div className="relative group">
          {canLeft && (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll thumbnails left"
              className="hidden md:grid absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 place-items-center rounded-full bg-white/95 border border-border shadow-md hover:bg-white transition"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Scroll thumbnails right"
              className="hidden md:grid absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 place-items-center rounded-full bg-white/95 border border-border shadow-md hover:bg-white transition"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {gallery.map((url) => {
              const isActive = activeImage === url;
              return (
                <button
                  key={url}
                  type="button"
                  onClick={() => setActiveImage(url)}
                  className={`shrink-0 snap-start h-16 w-16 md:h-20 md:w-20 rounded-xl overflow-hidden bg-white border-2 p-1 transition-all ${
                    isActive
                      ? "border-[hsl(24,95%,53%)] ring-2 ring-[hsl(24,95%,53%)]/30"
                      : "border-border hover:border-foreground/20"
                  }`}
                >
                  <img src={url} alt="" loading="lazy" className="h-full w-full object-contain" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {lightboxOpen && activeImage && (
        <Lightbox
          images={gallery}
          index={activeIndex}
          onClose={() => setLightboxOpen(false)}
          onChange={(i) => setActiveImage(gallery[i])}
          alt={product.name}
        />
      )}
    </div>
  );
};

/* -------------------- Lightbox -------------------- */

interface LightboxProps {
  images: string[];
  index: number;
  onClose: () => void;
  onChange: (i: number) => void;
  alt?: string;
}

const Lightbox = ({ images, index, onClose, onChange, alt }: LightboxProps) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const touchState = useRef<{
    mode: "none" | "pan" | "pinch" | "swipe";
    startX: number;
    startY: number;
    lastTap: number;
    pinchStartDist: number;
    pinchStartScale: number;
    startOffset: { x: number; y: number };
  }>({
    mode: "none",
    startX: 0,
    startY: 0,
    lastTap: 0,
    pinchStartDist: 0,
    pinchStartScale: 1,
    startOffset: { x: 0, y: 0 },
  });

  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    resetZoom();
  }, [index]);

  const next = () => onChange((index + 1) % images.length);
  const prev = () => onChange((index - 1 + images.length) % images.length);

  const dist = (a: React.Touch, b: React.Touch) =>
    Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      touchState.current.mode = "pinch";
      touchState.current.pinchStartDist = dist(e.touches[0], e.touches[1]);
      touchState.current.pinchStartScale = scale;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      const now = Date.now();
      if (now - touchState.current.lastTap < 280) {
        // double tap
        if (scale > 1) {
          resetZoom();
        } else {
          setScale(2.2);
        }
        touchState.current.lastTap = 0;
        return;
      }
      touchState.current.lastTap = now;
      touchState.current.startX = t.clientX;
      touchState.current.startY = t.clientY;
      touchState.current.startOffset = { ...offset };
      touchState.current.mode = scale > 1 ? "pan" : "swipe";
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const st = touchState.current;
    if (st.mode === "pinch" && e.touches.length === 2) {
      const d = dist(e.touches[0], e.touches[1]);
      const ns = Math.max(1, Math.min(4, st.pinchStartScale * (d / st.pinchStartDist)));
      setScale(ns);
      if (ns === 1) setOffset({ x: 0, y: 0 });
    } else if (st.mode === "pan" && e.touches.length === 1) {
      const t = e.touches[0];
      setOffset({
        x: st.startOffset.x + (t.clientX - st.startX),
        y: st.startOffset.y + (t.clientY - st.startY),
      });
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const st = touchState.current;
    if (st.mode === "swipe" && e.changedTouches.length === 1) {
      const t = e.changedTouches[0];
      const dx = t.clientX - st.startX;
      const dy = t.clientY - st.startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) next();
        else prev();
      }
    }
    st.mode = "none";
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col animate-fade-in"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="text-sm font-medium tabular-nums">
          {index + 1} / {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid h-10 w-10 place-items-center rounded-full bg-white/10 hover:bg-white/20 transition"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Image area */}
      <div
        className="relative flex-1 overflow-hidden flex items-center justify-center select-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={() => (scale > 1 ? resetZoom() : setScale(2.2))}
        style={{ touchAction: "none" }}
      >
        <img
          src={images[index]}
          alt={alt}
          draggable={false}
          className="max-h-full max-w-full object-contain transition-transform duration-200 ease-out"
          style={{
            transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
            cursor: scale > 1 ? "grab" : "zoom-in",
          }}
        />

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous image"
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 grid h-11 w-11 md:h-12 md:w-12 place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 grid h-11 w-11 md:h-12 md:w-12 place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="px-4 pb-4 pt-2">
          <div className="flex gap-2 overflow-x-auto justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {images.map((u, i) => (
              <button
                key={u}
                type="button"
                onClick={() => onChange(i)}
                className={`shrink-0 h-16 w-16 rounded-lg overflow-hidden border-2 bg-white p-1 transition ${
                  i === index ? "border-white" : "border-white/20 hover:border-white/60 opacity-70 hover:opacity-100"
                }`}
              >
                <img src={u} alt="" loading="lazy" className="h-full w-full object-contain" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
